import { shapes } from '@joint/plus';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
    Autocomplete, Box, Chip, CircularProgress, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import React, { ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';

import * as TagService from '../../../../../../../../services/2talk/tag.service.js';
import { useBaseInspector } from './useBaseInspector';

/**
 * TagInspector — Item #2 roadmap chatbot JOINT (07/10/2026).
 *
 * Caixa SÍNCRONA que aplica N tags cadastradas ao chat corrente. Admin escolhe tags
 * existentes OU digita nome novo (freeSolo) + Enter → cadastra on-the-fly via POST /tags
 * e já aparece como chip selecionado.
 *
 * UX decidida com user:
 *   - Autocomplete multiple + freeSolo + chips
 *   - Opção já cadastrada: select → chip
 *   - Nome novo + Enter: dispara POST /tags → recarrega lista → chip aparece (idempotente:
 *     se nome já existir, só seleciona)
 *   - Loading visual (CircularProgress) enquanto GET /tags carrega — nunca pisca erro
 *     por race (lição aprendida no SetVariableInspector)
 *
 * Persistência: `attrs/tagNames/text` como JSON array de strings (ex.: `["lead","vip"]`).
 * Backend: `ChatbotFlowController` case `FlowTypeTag` deserializa e chama ITagBoxService.
 */

interface Props {
    cell: shapes.app.Base;
    chatbotFlowId: string;
}

type TagOption = { id: number; name: string };

const cellProps = {
    label: ['attrs', 'label', 'text'],
    tagNamesJson: ['attrs', 'tagNames', 'text'],
};

const TagInspector = (props: Props): ReactElement => {
    const { cell } = props;
    const intl = useIntl();

    const [label, setLabel] = useState<string>('');
    const [selectedNames, setSelectedNames] = useState<string[]>([]);
    const [options, setOptions] = useState<TagOption[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [creating, setCreating] = useState<boolean>(false);

    const isSyncingRef = useRef(false);
    const initializedRef = useRef(false);

    // Carrega lista de tags do hirer.
    const loadTags = useCallback(async () => {
        setLoading(true);
        try {
            const resp: any = await TagService.getAll();
            const raw: any[] = Array.isArray(resp?.data) ? resp.data : [];
            const list: TagOption[] = raw
                .map((t) => ({ id: Number(t?.id ?? t?.Id ?? 0), name: String(t?.name ?? t?.Name ?? '') }))
                .filter((t) => !!t.name)
                .sort((a, b) => a.name.localeCompare(b.name));
            setOptions(list);
        } catch {
            setOptions([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { loadTags(); }, [loadTags]);

    const assignFormFields = useCallback((): void => {
        if (isSyncingRef.current) return;

        setLabel(cell.prop(cellProps.label) ?? '');
        const raw = cell.prop(cellProps.tagNamesJson);
        if (!raw) return;
        try {
            const parsed: any = JSON.parse(raw);
            if (Array.isArray(parsed)) {
                setSelectedNames(parsed.map((n) => String(n)).filter((n) => !!n));
            }
        } catch { /* mantém state atual */ }
    }, [cell]);

    const changeCellProp = useBaseInspector({ cell, assignFormFields });

    const persist = useCallback((names: string[]) => {
        isSyncingRef.current = true;
        try {
            // Deduplica ignore-case antes de serializar.
            const deduped = Array.from(new Map(names.map((n) => [n.toLowerCase(), n])).values());
            changeCellProp(cellProps.tagNamesJson, JSON.stringify(deduped));
        } finally {
            queueMicrotask(() => { isSyncingRef.current = false; });
        }
    }, [changeCellProp]);

    useEffect(() => {
        if (initializedRef.current) return;
        initializedRef.current = true;
        // Nada a inicializar — array vazio é default válido.
    }, []);

    // Handler central do Autocomplete multiple. Quando admin digita e dá Enter:
    //   - MUI entrega o valor novo COMO STRING (porque freeSolo)
    //   - Procuramos se já existe (case-insensitive) nas options → se sim, usa a opção
    //     existente; se não, dispara POST /tags em background, aguarda, recarrega lista
    //     e inclui o nome criado.
    const handleChange = async (_: any, values: Array<TagOption | string>) => {
        // Normaliza pra array de strings (name) com dedup ignore-case.
        const normalized: string[] = [];
        for (const v of values) {
            const name = typeof v === 'string' ? v.trim() : v?.name?.trim();
            if (!name) continue;
            if (normalized.some((n) => n.toLowerCase() === name.toLowerCase())) continue;
            normalized.push(name);
        }

        // Detecta nomes NOVOS (não presentes nas options atuais) pra disparar create.
        const newNames = normalized.filter(
            (n) => !options.some((o) => o.name.toLowerCase() === n.toLowerCase())
        );

        // Atualiza state imediato (chip aparece já) + persiste.
        setSelectedNames(normalized);
        persist(normalized);

        if (newNames.length === 0) return;

        // Cria cada nome novo em background. POST /tags é idempotente — se já existir
        // (concorrência / usuário rápido), não dá erro.
        setCreating(true);
        try {
            await Promise.all(newNames.map(async (name) => {
                try {
                    await TagService.create(name);
                } catch {
                    // Mesmo que o POST falhe, o chip continua selecionado. Em runtime, o backend
                    // (ITagBoxService) ainda faz find-or-create defensivo via InsertTagWithChatTagsAsync.
                }
            }));
            await loadTags();
        } finally {
            setCreating(false);
        }
    };

    return (
        <>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, p: 1 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="h6" sx={{ fontSize: 16, fontWeight: 600 }}>
                        {intl.formatMessage({ id: 'tag.title', defaultMessage: 'Tag' })}
                    </Typography>
                    <Tooltip title={intl.formatMessage({
                        id: 'tag.title.hint',
                        defaultMessage: 'Classifica a conversa aplicando uma ou mais tags cadastradas. Digite um nome novo e pressione Enter para criar on-the-fly.',
                    })}>
                        <InfoOutlinedIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </Tooltip>
                </Stack>

                <TextField
                    label={intl.formatMessage({ id: 'tag.label', defaultMessage: 'Rótulo (opcional)' })}
                    size="small"
                    value={label}
                    onChange={(e) => {
                        setLabel(e.target.value);
                        changeCellProp(cellProps.label, e.target.value);
                    }}
                    placeholder="Tag"
                />

                <Autocomplete<TagOption, true, false, true>
                    multiple
                    freeSolo
                    options={options}
                    value={selectedNames.map((n) => options.find((o) => o.name === n) ?? n) as any}
                    getOptionLabel={(o) => typeof o === 'string' ? o : o?.name ?? ''}
                    isOptionEqualToValue={(a, b) => {
                        const an = typeof a === 'string' ? a : a?.name;
                        const bn = typeof b === 'string' ? b : b?.name;
                        return an === bn;
                    }}
                    loading={loading}
                    loadingText={intl.formatMessage({ id: 'tag.loading', defaultMessage: 'Carregando tags...' })}
                    noOptionsText={intl.formatMessage({
                        id: 'tag.empty',
                        defaultMessage: 'Nenhuma tag cadastrada — digite e pressione Enter para criar.',
                    })}
                    onChange={handleChange}
                    renderTags={(value, getTagProps) =>
                        value.map((v, index) => {
                            const name = typeof v === 'string' ? v : v?.name;
                            return (
                                <Chip
                                    {...getTagProps({ index })}
                                    key={name}
                                    label={name}
                                    size="small"
                                    sx={{
                                        background: '#8BC34A',
                                        color: '#fff',
                                        fontWeight: 500,
                                        // Fix 09/10/2026 — MuiChip-deleteIcon default = rgba(0,0,0,0.26)
                                        // (invisivel sobre o verde). Força branco translucido com
                                        // hover solid branco pra deixar o X visivel/clicavel.
                                        '& .MuiChip-deleteIcon': {
                                            color: 'rgba(255,255,255,0.75)',
                                            '&:hover': { color: '#fff' },
                                        },
                                    }}
                                />
                            );
                        })
                    }
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            label={intl.formatMessage({ id: 'tag.autocomplete.label', defaultMessage: 'Tags' })}
                            placeholder={loading
                                ? intl.formatMessage({ id: 'tag.loading', defaultMessage: 'Carregando tags...' })
                                : intl.formatMessage({ id: 'tag.autocomplete.placeholder', defaultMessage: 'Escolha ou digite para criar + Enter' })}
                            InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                    <>
                                        {(loading || creating)
                                            ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} />
                                            : null}
                                        {params.InputProps.endAdornment}
                                    </>
                                ),
                            }}
                        />
                    )}
                />
            </Box>
        </>
    );
};

export default TagInspector;
